import React from 'react'
import classNames from 'classnames'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { currencyFormatter, convertToDomain } from '../utlis/utlis'
import { useTheme } from '../context/themeContext'
import Flag from 'react-flagkit'
import { MemoChart } from './ProductGrid'
import Pill from './Pill'
import Tippy from '@tippyjs/react'
import 'tippy.js/dist/tippy.css'
import { ReactComponent as AmazonIcon } from '../icons/amazon.svg'
import { ReactComponent as ExportIcon } from '../icons/export.svg'
import { ReactComponent as A1Icon } from '../icons/a1.svg'
import { ReactComponent as SasIcon } from '../icons/sas.svg'
import { ReactComponent as TrashIcon } from '../icons/trash.svg'
import { ReactComponent as ExchangeIcon } from '../icons/exchange.svg'
import FavoriteStar from './FavoriteStar'
import MismatchIcon from './MismatchIcon'
import PaginationBar from './PaginationBar'

const metricKeys = [
  { label: 'margin', key: 'margin' },
  { label: 'profit', key: 'profit', prefix: '€' },
  { label: 'buy', key: 'buyprice', prefix: '€' },
  { label: 'sell', key: 'sellprice', prefix: '€' },
  { label: 'asin', key: 'asin' },
  { label: 'margin 30d', key: 'margin30' },
  { label: 'profit 30d', key: 'profit30', prefix: '€' },
  { label: 'bsr', key: 'salesrank' },
]

const colorByMargin = (value) => {
  if (value === undefined || value === null || isNaN(value)) return 'text-transparent'
  if (value >= 10) return 'dark:bg-text-green-dark bg-text-green-light text-transparent'
  if (value >= 2) return 'dark:bg-text-orange-dark bg-text-orange-light text-transparent'
  return 'dark:bg-text-red-dark bg-text-red-light text-transparent'
}

const bgColorByMargin = (value) => {
  if (value === undefined || value === null || isNaN(value)) return 'dark:!bg-[#121215] bg-white'
  if (value >= 10) return '!bg-bg-green-gradient-light dark:!bg-bg-green-gradient-dark bg-white'
  if (value >= 2) return '!bg-bg-orange-gradient-light dark:bg-bg-orange-gradient-dark bg-white'
  return '!bg-bg-red-gradient-light dark:bg-bg-red-gradient-dark bg-white'
}

const CardSkeleton = () =>
  Array.from({ length: 4 }).map((_, i) => (
    <div key={i} className='grid grid-cols-1'>
      <div className='rounded-2xl p-4 bg-white dark:bg-[#0b0e14] shadow-[0_12px_28px_-16px_rgba(0,0,0,0.75)] border border-gray-200 dark:border-[#111725] mx-[4px]'>
        {/* Titel + Timestamp */}
        <div className='flex items-start justify-between mb-3'>
          <div className='animate-pulse h-5 w-[280px] bg-gray-200 dark:bg-gray-700 rounded' />
          <div className='animate-pulse h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded shrink-0' />
        </div>
        <div className='grid gap-4 items-center' style={{ gridTemplateColumns: '160px 110px 60px 1fr 140px 400px' }}>
          {/* Bild */}
          <div className='flex items-center justify-center'>
            <div className='animate-pulse h-[130px] w-[150px] bg-gray-200 dark:bg-gray-700 rounded-xl' />
          </div>
          {/* Buy Button + Buyprice gestapelt */}
          <div className='flex flex-col items-center justify-center gap-2'>
            <div className='animate-pulse h-7 w-full bg-gray-200 dark:bg-gray-700 rounded-md' />
            <div className='animate-pulse h-7 w-full bg-gray-200 dark:bg-gray-700 rounded-full' />
          </div>
          {/* Action Buttons */}
          <div className='flex flex-row gap-1 justify-end items-start'>
            <div className='flex flex-col gap-1'>
              {Array.from({ length: 2 }).map((_, j) => (
                <div key={j} className='animate-pulse h-6 w-6 bg-gray-200 dark:bg-gray-700 rounded-md' />
              ))}
            </div>
            <div className='flex flex-col gap-1'>
              {Array.from({ length: 4 }).map((_, j) => (
                <div key={j} className='animate-pulse h-6 w-6 bg-gray-200 dark:bg-gray-700 rounded-md' />
              ))}
            </div>
          </div>
          {/* Metriken */}
          <div className='flex flex-col gap-0.5 pr-2'>
            <div className='grid grid-cols-[60px_104px_104px_104px_104px_82px_60px] gap-1 mb-1'>
              <div />
              {Array.from({ length: 6 }).map((_, j) => (
                <div key={j} className='animate-pulse h-2.5 w-[50px] bg-gray-200 dark:bg-gray-700 rounded mx-auto' />
              ))}
            </div>
            {Array.from({ length: 4 }).map((_, idx) => (
              <div key={idx} className='grid grid-cols-[60px_104px_104px_104px_104px_82px_60px] gap-1 items-center'>
                <div className='animate-pulse h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded ml-auto' />
                {Array.from({ length: 6 }).map((_, j) => (
                  <div key={j} className='animate-pulse h-7 w-full bg-gray-200 dark:bg-gray-700 rounded-full' />
                ))}
              </div>
            ))}
          </div>
          {/* ASIN / EAN */}
          <div className='flex flex-col gap-3'>
            {Array.from({ length: 2 }).map((_, j) => (
              <div key={j} className='flex flex-col gap-1'>
                <div className='animate-pulse h-2.5 w-8 bg-gray-200 dark:bg-gray-700 rounded' />
                <div className='animate-pulse h-7 w-full bg-gray-200 dark:bg-gray-700 rounded-full' />
              </div>
            ))}
          </div>
          {/* Chart */}
          <div className='animate-pulse h-[165px] w-full bg-gray-200 dark:bg-gray-700 rounded-lg' />
        </div>
      </div>
    </div>
  ))

const Metric = ({ label, value, marginBase }) => {
  const isMarginRelated = ['margin', 'margin 30d', 'profit', 'profit 30d'].includes(label)
  const bgClass = isMarginRelated ? bgColorByMargin(marginBase) : 'dark:!bg-[#121215] bg-white'
  const textClass = isMarginRelated ? colorByMargin(marginBase) : 'dark:bg-text-simple-dark bg-text-simple-light text-transparent'

  return (
    <div className='text-left min-w-20'>
      <p className='subtitle text-[11px] uppercase flex'>{label}</p>
      <div className={classNames('rounded text-center shadow-[0px_0px_5px_0px_#0000001A]', bgClass)}>
        <span
          className={classNames('block truncate p_detail px-2 pt-[4px] pb-[3px] text-[12px] text-primarytextColor', textClass)}
          style={{ backgroundClip: 'text' }}
        >
          {value ?? '-'}
        </span>
      </div>
    </div>
  )
}

const ProductCard = ({ 
  product, 
  regionEntries, 
  theme, 
  use30d,
  currentUse30d,
  handleToggle30d,
  application,
  regions,
  isPregenListFilterActive,
  isWholesaleMode,
  handleMismatchToggle,
  handleFavoriteToggle,
  handleFavoriteModalOpen,
  handleDeleteAsin,
  favoritePendingAsins,
  mismatchPendingAsins,
  addSupplierToBlacklistMutation,
  resolveAsin,
  resolveMismatchActive,
  copiedASIN,
  setCopiedASIN
}) => {
  const primaryRegionKey = regionEntries[0]?.[0] || null
  const primaryRegionData = regionEntries[0]?.[1] || null

  const chartSrc =
    (primaryRegionKey &&
      product.keepa_cahrts?.[primaryRegionKey]?.[theme === 'dark' ? 'dark' : 'white']?.[90]) ??
    product.keepa_chart_dk ??
    'static/NotAvailableChart-White.png'

  const baseBuyUrl = product.supplier_url
  const baseSellUrl = product.amz_url

  const valueByMeta = (regionDataEntry, metaKey) => {
    switch (metaKey) {
      case 'asin':
        return product.asin
      case 'buyprice':
        return regionDataEntry?.buyprice ?? product.buyprice
      case 'sellprice':
        return use30d
          ? regionDataEntry?.sellprice30 ?? regionDataEntry?.sellprice
          : regionDataEntry?.sellprice ?? product.sellprice
      case 'margin':
        return use30d
          ? regionDataEntry?.margin30 ?? regionDataEntry?.margin
          : regionDataEntry?.margin ?? product.margin
      case 'profit':
        return use30d
          ? regionDataEntry?.profit30 ?? regionDataEntry?.profit
          : regionDataEntry?.profit ?? product.profit
      case 'margin30':
        return regionDataEntry?.margin30 ?? product.margin30
      case 'profit30':
        return regionDataEntry?.profit30 ?? product.profit30
      case 'salesrank':
        return regionDataEntry?.salesrank ?? product.salesrank
      default:
        return regionDataEntry?.[metaKey] ?? product?.[metaKey]
    }
  }

  const normalizeVal = (v) => (v === undefined || v === null ? null : Number.isNaN(Number(v)) ? v : Number(v))

  const variantsByKey = metricKeys.reduce((acc, meta) => {
    const values = regionEntries.map(([, regionDataEntry]) =>
      normalizeVal(valueByMeta(regionDataEntry, meta.key))
    )
    const first = values[0]
    acc[meta.key] = values.some((val) => val !== first)
    return acc
  }, {})

  const sharedMetas = metricKeys.filter((meta) => !variantsByKey[meta.key])
  const variantMetas = metricKeys.filter((meta) => variantsByKey[meta.key])

  const buyVaries = regionEntries
    .map(([, data]) => normalizeVal(data?.buyprice ?? product.buyprice))
    .some((v, _, arr) => v !== arr[0])

  const sellVaries = regionEntries
    .map(([, data]) => normalizeVal(use30d ? data?.sellprice30 ?? data?.sellprice : data?.sellprice ?? product.sellprice))
    .some((v, _, arr) => v !== arr[0])

  return (
    <div className='grid grid-cols-1'>
      <div className='rounded-2xl p-4 bg-white dark:bg-[#0b0e14] text-gray-900 dark:text-white shadow-[0_12px_28px_-16px_rgba(0,0,0,0.75)] border border-gray-200 dark:border-[#111725] mx-[4px] relative'>
        {/* Timestamp oben rechts */}
        <div className='flex items-start justify-between mb-3'>
          <p className='text-[20px] font-semibold leading-tight text-gray-900 dark:text-[#f7f8fb] pr-4'>{product.name?.length > 37 ? `${product.name.slice(0, 37)}…` : product.name}</p>
          {product.timestamp && (
            <span className='text-[14px] text-gray-400 dark:text-gray-300 shrink-0 font-medium'>{product.timestamp}</span>
          )}
        </div>
        <div
          className='grid gap-4 items-center'
          style={{ gridTemplateColumns: '160px 110px 60px 1fr 140px 400px' }}
        >
          {/* Spalte 1: nur Produktbild */}
          <div className='flex items-center justify-center'>
            <div className='h-[130px] w-[150px] rounded-xl bg-gray-100 dark:bg-[#101420] overflow-hidden flex items-center justify-center border border-gray-300 dark:border-[#1a2234]'>
              <img
                src={product.amz_img || product.img || 'static/NotAvailableChart-White.png'}
                alt='product'
                className='h-full w-full object-contain'
              />
            </div>
          </div>
          {/* Spalte 2: Buy-Button + Buyprice gestapelt */}
          <div className='flex flex-col items-center justify-center gap-2'>
            {baseBuyUrl ? (() => {
              const supplierNameRaw = product?.supplier_name
              const supplierName = typeof supplierNameRaw === 'string' ? supplierNameRaw.trim() : ''
              const hasSupplierName = supplierName && supplierName !== 'N/A'
              let host = ''
              let link = baseBuyUrl
              try {
                const parsed = new URL(baseBuyUrl)
                const rawHost = parsed.hostname.replace(/^www\./, '')
                host = rawHost
                if (host === 'dealsdirekt.de') {
                  const target = parsed.searchParams.get('url')
                  if (target) {
                    try {
                      host = new URL(target).hostname.replace(/^www\./, '')
                    } catch {
                      // ignore
                    }
                  }
                }
                if (host.startsWith('amazon.') && rawHost !== 'dealsdirekt.de') {
                  link = `https://dealsdirekt.de/go.html?url=${encodeURIComponent(baseBuyUrl)}`
                }
              } catch {
                // ignore
              }
              const displayName = hasSupplierName ? supplierName : (host || 'Buy')
              return (
                <button
                  key='buy'
                  className='w-full h-7 px-2 rounded-md bg-gradient-to-br from-primary to-main text-white dark:text-black font-semibold text-xs leading-tight truncate'
                  onClick={() => window.open(link, '_blank')}
                >
                  {displayName}
                </button>
              )
            })() : <span className='text-[10px] text-gray-500'>–</span>}
            <Pill className='px-2 py-1.5 text-xs w-full justify-center'>
              {(product.buyprice !== null && product.buyprice !== undefined) ? `${currencyFormatter(product.buyprice)}€` : '-'}
            </Pill>
          </div>
          {/* Spalte 4: Action-Buttons modular in 3 vertikale Reihen verteilt */}
          {(() => {
            const btn = (content, children, key) => (
              <Tippy key={key} content={content} placement='top'>
                {children}
              </Tippy>
            )
            const actionButtons = []
            if (primaryRegionData?.amz_url || product.amz_url) {
              actionButtons.push(btn('Amazon öffnen', (
                <a
                  href={`https://dealsdirekt.de/go.html?url=${encodeURIComponent(primaryRegionData?.amz_url || product.amz_url)}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='w-6 h-6 bg-primary text-white dark:bg-secondary dark:text-primary flex items-center justify-center rounded-md cursor-pointer hover:opacity-80 transition-opacity'
                >
                  <AmazonIcon className='w-4 h-4' />
                </a>
              ), 'amazon'))
            }
            actionButtons.push(btn('Store & Amazon öffnen', (
              <button
                type='button'
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  if (product.supplier_url) window.open(product.supplier_url, '_blank')
                  regions.forEach(region => {
                    const regionData = product.regions?.[region]
                    if (regionData?.amz_url) {
                      window.open(`https://dealsdirekt.de/go.html?url=${encodeURIComponent(regionData.amz_url)}`, '_blank')
                    }
                  })
                }}
                className='w-6 h-6 bg-primary text-white dark:bg-secondary dark:text-primary flex items-center justify-center rounded-md cursor-pointer hover:opacity-80 transition-opacity'
              >
                <ExportIcon className='w-4 h-4' />
              </button>
            ), 'export'))
            if (application === 'pricealerts' && !isPregenListFilterActive && handleDeleteAsin) {
              actionButtons.push(btn('Preiswecker löschen', (
                <button
                  onClick={async () => {
                    if (product.asin && handleDeleteAsin) await handleDeleteAsin(product.asin)
                  }}
                  className='w-6 h-6 bg-primary dark:bg-secondary dark:text-primary text-white flex items-center justify-center rounded-md'
                >
                  <TrashIcon className='w-4 h-4' />
                </button>
              ), 'trash'))
            }
            if (application === 'pricealerts' && product.supplier_url && addSupplierToBlacklistMutation) {
              actionButtons.push(btn('Supplier zur Blacklist hinzufügen', (
                <button
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    const url = product.supplier_url
                    const supplierNameRaw = product?.supplier_name
                    const supplierName = typeof supplierNameRaw === 'string' ? supplierNameRaw.trim() : ''
                    let finalSupplierName = supplierName
                    if (!finalSupplierName || finalSupplierName === 'N/A') {
                      try {
                        const parsed = new URL(url)
                        let extractedHost = parsed.hostname.replace(/^www\./, '')
                        if (extractedHost === 'dealsdirekt.de') {
                          const target = parsed.searchParams.get('url')
                          if (target) {
                            try {
                              extractedHost = new URL(target).hostname.replace(/^www\./, '')
                            } catch {
                              // ignore
                            }
                          }
                        }
                        finalSupplierName = extractedHost
                      } catch {
                        // ignore
                      }
                    }
                    if (finalSupplierName && addSupplierToBlacklistMutation) {
                      addSupplierToBlacklistMutation.mutate({
                        filter_id: 'supplier',
                        filter_type: '∉',
                        attribute: finalSupplierName
                      })
                    }
                  }}
                  disabled={addSupplierToBlacklistMutation?.isPending}
                  className='w-6 h-6 bg-primary text-white dark:bg-secondary dark:text-primary flex items-center justify-center rounded-md disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  <svg className='w-4 h-4' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path d='M 4 8 L 2 4' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
                    <rect x='4' y='8' width='14' height='10' stroke='currentColor' strokeWidth='1.5' fill='none' />
                    <circle cx='16' cy='20' r='2' fill='currentColor' />
                    <circle cx='6' cy='20' r='2' fill='currentColor' />
                  </svg>
                </button>
              ), 'blacklist'))
            }
            if (product.a1_url) {
              actionButtons.push(btn('A1 öffnen', (
                <a href={product.a1_url} target='_blank' rel='noopener noreferrer' className='w-6 h-6 bg-primary flex items-center justify-center rounded-md cursor-pointer hover:opacity-80 transition-opacity'>
                  <A1Icon className='w-5 h-5 dark:invert' />
                </a>
              ), 'a1'))
            }
            if (product.asin) {
              actionButtons.push(btn('Selleramp öffnen', (
                <a
                  href={`https://sas.selleramp.com/sas/lookup/?asin=${product.asin}&sas_cost_price=${product.buyprice || ''}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='w-6 h-6 bg-primary text-white dark:bg-secondary dark:text-primary flex items-center justify-center rounded-md cursor-pointer hover:opacity-80 transition-opacity'
                >
                  <SasIcon className='w-6 h-6 [--sas-bg:#3d84ff] [--sas-text:#ffffff] dark:[--sas-bg:#0a241e] dark:[--sas-text:#2effc0]' />
                </a>
              ), 'sas'))
            }
            if (handleMismatchToggle) {
              actionButtons.push(btn(resolveMismatchActive?.(product.mismatch) ? 'Deal als Mismatch entfernen' : 'Deal als Mismatch markieren', (
                <button
                  type='button'
                  onClick={() => handleMismatchToggle(product)}
                  disabled={resolveAsin?.(product) ? mismatchPendingAsins?.[resolveAsin(product)] : false}
                  className={classNames(
                    'w-6 h-6 bg-primary dark:bg-secondary dark:text-primary text-white flex items-center justify-center rounded-md',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  <MismatchIcon />
                </button>
              ), 'mismatch'))
            }
            if (!isWholesaleMode && handleFavoriteToggle) {
              actionButtons.push(btn(product.favorite ? 'Deal aus Favoriten entfernen' : 'Deal als Favorit markieren', (
                <button
                  type='button'
                  onClick={() => handleFavoriteToggle(product)}
                  disabled={resolveAsin?.(product) ? favoritePendingAsins?.[resolveAsin(product)] : false}
                  className={classNames(
                    'w-6 h-6 flex items-center justify-center rounded-md transition border',
                    'bg-white text-primary border-primary/40 hover:bg-primary/10',
                    'dark:bg-secondary dark:text-primary dark:border-transparent',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  <FavoriteStar active={!!product.favorite} />
                </button>
              ), 'favorite'))
            }
            if (isWholesaleMode && handleFavoriteModalOpen) {
              actionButtons.push(btn('Deal als Favorit markieren', (
                <button
                  type='button'
                  onClick={() => handleFavoriteModalOpen(product)}
                  className={classNames(
                    'w-6 h-6 flex items-center justify-center rounded-md transition border',
                    'bg-white text-primary border-primary/40 hover:bg-primary/10',
                    'dark:bg-secondary dark:text-primary dark:border-transparent'
                  )}
                >
                  <FavoriteStar active={!!product.favorite} />
                </button>
              ), 'favorite-modal'))
            }
            const rightCol = actionButtons.slice(Math.max(0, actionButtons.length - 4))
            const leftCol = actionButtons.slice(0, Math.max(0, actionButtons.length - 4))
            return (
              <div className='flex flex-row gap-1 justify-end items-start'>
                {leftCol.length > 0 && (
                  <div className='flex flex-col gap-1'>
                    {leftCol}
                  </div>
                )}
                <div className='flex flex-col gap-1'>
                  {rightCol}
                </div>
              </div>
            )
          })()}

          {/* Metriken (bleibt gleich) */}
          <div className='flex flex-col gap-0.5 justify-center pr-2 relative'>
            <div className='grid grid-cols-[60px_104px_104px_104px_104px_82px_60px] gap-1 text-[11px] uppercase tracking-[0.08em] text-gray-600 dark:text-white/60 pr-1'>
              <span></span>
              <div className='flex items-center justify-center gap-1 cursor-pointer'>
                <span className={!currentUse30d ? '' : 'opacity-50'}>Margin</span>
                <ExchangeIcon className='w-3 h-3' onClick={e => {
                  e.stopPropagation()
                  e.preventDefault()
                  handleToggle30d(v => !v)
                }} />
                <span className={currentUse30d ? '' : 'opacity-50'}>30D</span>
              </div>
              <div className='flex items-center justify-center gap-1 cursor-pointer'>
                <span className={!currentUse30d ? '' : 'opacity-50'}>Profit</span>
                <ExchangeIcon className='w-3 h-3' onClick={e => {
                  e.stopPropagation()
                  e.preventDefault()
                  handleToggle30d(v => !v)
                }} />
                <span className={currentUse30d ? '' : 'opacity-50'}>30D</span>
              </div>
              <div className='flex flex-col items-center leading-tight'>
                <span>Sales</span>
                <span className='text-orange-400 dark:text-orange-300 normal-case'>↓ Drops</span>
              </div>
              <div className='flex items-center justify-center gap-1 cursor-pointer'>
                <span className={!currentUse30d ? '' : 'opacity-50'}>Sell</span>
                <ExchangeIcon className='w-3 h-3' onClick={e => {
                  e.stopPropagation()
                  e.preventDefault()
                  handleToggle30d(v => !v)
                }} />
                <span className={currentUse30d ? '' : 'opacity-50'}>30D</span>
              </div>
              <span className='text-center'>BSR %</span>
              <span className='text-center'>Variations</span>
            </div>

            {regionEntries.map(([regionKey, regionDataEntry], idx) => {
              const regionMargin = valueByMeta(regionDataEntry, 'margin')
              const regionProfit = valueByMeta(regionDataEntry, 'profit')
              const regionSales = regionDataEntry?.confirmed_sales ?? product?.confirmed_sales ?? regionDataEntry?.estimated_sales ?? product?.estimated_sales ?? '-'
              const regionSalesdrops = regionDataEntry?.salesdrops ?? product?.salesdrops ?? null
              const regionSellPrice = valueByMeta(regionDataEntry, 'sellprice')
              const regionBsr = valueByMeta(regionDataEntry, 'percent_bsr') ?? regionDataEntry?.salesrank ?? product.salesrank ?? '-'
              const regionVariations = regionDataEntry?.variations ?? product?.variations ?? null
              const regionSellUrl = regionDataEntry?.amz_url || baseBuyUrl
              
              // Amazon URL für diese Region konstruieren
              const getAmazonUrl = (region) => {
                const regionAsin = regionDataEntry?.asin ?? product.asin
                const buyprice = regionDataEntry?.buyprice ?? product.buyprice
                if (regionAsin && region) {
                  return `https://dealsdirekt.de/go.html?url=https://www.amazon.${region}/dp/${regionAsin}${buyprice ? `?bbp-buyprice=${buyprice}&sas_cost_price=${buyprice}&th=1` : ''}`
                }
                if (regionDataEntry?.amz_url) {
                  return `https://dealsdirekt.de/go.html?url=${encodeURIComponent(regionDataEntry.amz_url)}`
                }
                return null
              }
              
              return (
                <div key={`${regionKey || idx}`} className='grid grid-cols-[60px_104px_104px_104px_104px_82px_60px] gap-1 items-center relative' style={{ minHeight: idx === 0 ? 'auto' : '28px' }}>
                  <div className='flex items-center gap-2 justify-end pr-1'>
                    {regionKey && (
                      <a
                        href={getAmazonUrl(regionKey)}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='cursor-pointer hover:opacity-80 transition-opacity'
                        onClick={(e) => {
                          const url = getAmazonUrl(regionKey)
                          if (!url) {
                            e.preventDefault()
                          }
                        }}
                      >
                        <Flag country={regionKey.toUpperCase()} size={20} />
                      </a>
                    )}
                  </div>
                  <Pill
                    className='px-4 py-[6px] text-sm w-full justify-center items-center leading-tight rounded-full'
                    color={
                      regionMargin >= 10 ? 'green' :
                      regionMargin >= 2 ? 'orange' : 'red'
                    }
                  >
                    {regionMargin !== null && regionMargin !== undefined ? `${Number(regionMargin).toFixed(2)}%` : '-'}
                  </Pill>
                  <Pill
                    className='px-4 py-[6px] text-sm w-full justify-center items-center leading-tight rounded-full'
                    color={
                      regionMargin >= 10 ? 'green' :
                      regionMargin >= 2 ? 'orange' : 'red'
                    }
                  >
                    {regionProfit !== null && regionProfit !== undefined ? `${currencyFormatter(Number(regionProfit))}€` : '-'}
                  </Pill>
                  <Pill className='px-2 py-[6px] text-sm w-full justify-center items-center leading-tight rounded-full gap-1'>
                    <span>{regionSales}</span>
                    {regionSalesdrops !== null && regionSalesdrops !== undefined && (
                      <span className='text-[12px] font-bold text-orange-400 dark:text-orange-300 bg-orange-100 dark:bg-orange-900/40 rounded px-1.5'>
                        ↓{regionSalesdrops}
                      </span>
                    )}
                  </Pill>
                  <Pill className='px-4 py-[6px] text-sm w-full justify-center items-center leading-tight rounded-full'>{regionSellPrice !== null && regionSellPrice !== undefined ? `${currencyFormatter(Number(regionSellPrice))}€` : '-'}</Pill>
                  <Pill className='px-4 py-[6px] text-sm w-full justify-center items-center leading-tight rounded-full'>{regionBsr}</Pill>
                  <Pill className='px-2 py-[6px] text-sm w-full justify-center items-center leading-tight rounded-full'>{(regionVariations !== null && regionVariations !== undefined && regionVariations !== 'N/A') ? regionVariations : '-'}</Pill>
                </div>
              )
            })}

          </div>

          {/* 3. ASIN und EAN übereinander gestapelt */}
          <div className='flex flex-col gap-3 justify-start'>
            {product.asin && (
              <div className='flex flex-col gap-1'>
                <span className='text-[10px] uppercase tracking-[0.08em] text-gray-600 dark:text-white/60'>ASIN</span>
                <div className='relative'>
                  <Pill 
                    className='px-2 py-1.5 text-xs text-center cursor-pointer hover:opacity-80 transition-opacity'
                    onClick={() => {
                      navigator.clipboard.writeText(product.asin).then(() => {
                        if (setCopiedASIN) {
                          setCopiedASIN(product.asin)
                          setTimeout(() => setCopiedASIN(null), 2000)
                        }
                      })
                    }}
                  >
                    {product.asin}
                  </Pill>
                  {copiedASIN === product.asin && (
                    <span className='absolute -top-6 left-1/2 transform -translate-x-1/2 text-[10px] text-green-500 whitespace-nowrap'>
                      Kopiert!
                    </span>
                  )}
                </div>
              </div>
            )}
            {product.ean && (
              <div className='flex flex-col gap-1'>
                <span className='text-[10px] uppercase tracking-[0.08em] text-gray-600 dark:text-white/60'>EAN</span>
                <Pill className='px-3 py-1.5 text-xs text-center'>{product.ean}</Pill>
              </div>
            )}
            {application === 'spy' && (() => {
              const trackedSellers = Array.isArray(product.tracked_sellers)
                ? product.tracked_sellers.filter((entry) => Array.isArray(entry) && (entry[0] || entry[1]))
                : []
              const sellerNames = trackedSellers.map(([, name]) => name).filter(Boolean)
              
              if (trackedSellers.length === 0) {
                const fallback = product.seller_name ?? product.seller ?? null
                return fallback ? (
                  <div className='flex flex-col gap-1'>
                    <span className='text-[10px] uppercase tracking-[0.08em] text-gray-600 dark:text-white/60'>Seller</span>
                    <Pill className='px-3 py-1.5 text-xs text-center'>{fallback}</Pill>
                  </div>
                ) : null
              }
              
              const displayName = sellerNames[0] || product.seller_name || product.seller || '-'
              const extraCount = sellerNames.length - 1
              const pillLabel = extraCount > 0 ? `${displayName} +${extraCount}` : displayName
              
              return (
                <div className='flex flex-col gap-1'>
                  <span className='text-[10px] uppercase tracking-[0.08em] text-gray-600 dark:text-white/60'>Seller</span>
                  <Tippy
                    placement='bottom'
                    content={
                      <div className='flex flex-col gap-1 text-xs'>
                        {trackedSellers.map(([id, name], index) => (
                          <span key={id || `${name}-${index}`}>
                            {name || '-'} {id ? `(${id})` : ''}
                          </span>
                        ))}
                      </div>
                    }
                  >
                    <span>
                      <Pill className='px-3 py-1.5 text-xs text-center'>{pillLabel}</Pill>
                    </span>
                  </Tippy>
                </div>
              )
            })()}
          </div>

          {/* 4. Chart zuletzt */}
          <div className='flex flex-col gap-3'>
            {product.keepa_cahrts ? (
              <MemoChart
                charts={product.keepa_cahrts}
                regions={regionEntries.map(([regionKey]) => regionKey).filter(Boolean)}
              />
            ) : (
              <img
                src={chartSrc}
                alt='product_chart'
                className='w-full h-[165px] rounded-lg object-contain bg-gray-100 dark:bg-[#0f141e] border border-gray-300 dark:border-[#182235]'
              />
            )}
          </div>

        </div>
      </div>
    </div>
  )
}

export default function ProductCardView({ 
  products = [], 
  regions = [], 
  use30d = false, 
  setUse30d,
  loading = false,
  application,
  isPregenListFilterActive = false,
  isWholesaleMode = false,
  handleMismatchToggle,
  handleFavoriteToggle,
  handleFavoriteModalOpen,
  handleDeleteAsin,
  favoritePendingAsins = {},
  mismatchPendingAsins = {},
  addSupplierToBlacklistMutation,
  resolveAsin,
  resolveMismatchActive,
  pagination,
  data,
  totalPages,
  hasNext,
  setPagination,
  outerScroll = false,
  onPageViewportReset,
}) {
  const { theme } = useTheme()
  
  // Lokaler State falls setUse30d nicht übergeben wird
  const [localUse30d, setLocalUse30d] = React.useState(use30d)
  const currentUse30d = setUse30d ? use30d : localUse30d
  const handleToggle30d = setUse30d || setLocalUse30d
  
  // State für kopierte ASIN
  const [copiedASIN, setCopiedASIN] = React.useState(null)

  if (loading) {
    return <div className='px-4 md:px-8'>{<CardSkeleton />}</div>
  }

  const handlePageChange = (page) => {
    onPageViewportReset?.()
    setPagination({ ...pagination, pageIndex: page })
  }

  const handlePageSizeChange = (size) => {
    onPageViewportReset?.()
    setPagination({ pageIndex: 0, pageSize: size })
  }

  const getRegionEntries = (product) => {
    if (Array.isArray(regions) && regions.length && product?.regions) {
      const filtered = regions
        .filter((r) => product.regions[r])
        .map((r) => [r, product.regions[r]])
      if (filtered.length) return filtered
    }
    const entries = product?.regions ? Object.entries(product.regions) : []
    if (entries.length) return entries
    return [[null, null]]
  }

  // .product-section (style.css) bringt overflow:auto + max-height:80vh mit --
  // die Karten wuerden intern scrollen und Header/Filter der Page staenden fest.
  // Overrides daher IMMER aktiv (nicht nur bei outerScroll): die Liste waechst,
  // die Page-Section scrollt, oben scrollt alles smooth weg.
  return (
    <div className='product-section text-[13px] md:w-auto card-bottom z-0 px-4 md:px-8 !overflow-visible !h-auto !max-h-none'>
      <div className='list gap-6'>
        {products.map((product, idx) => {
          const regionEntries = getRegionEntries(product)
          return (
            <ProductCard
              key={`${product.asin}-${idx}`}
              product={product}
              regionEntries={regionEntries}
              theme={theme}
              use30d={currentUse30d}
              currentUse30d={currentUse30d}
              handleToggle30d={handleToggle30d}
              application={application}
              regions={regions}
              isPregenListFilterActive={isPregenListFilterActive}
              isWholesaleMode={isWholesaleMode}
              handleMismatchToggle={handleMismatchToggle}
              handleFavoriteToggle={handleFavoriteToggle}
              handleFavoriteModalOpen={handleFavoriteModalOpen}
              handleDeleteAsin={handleDeleteAsin}
              favoritePendingAsins={favoritePendingAsins}
              mismatchPendingAsins={mismatchPendingAsins}
              addSupplierToBlacklistMutation={addSupplierToBlacklistMutation}
              resolveAsin={resolveAsin}
              resolveMismatchActive={resolveMismatchActive}
              copiedASIN={copiedASIN}
              setCopiedASIN={setCopiedASIN}
            />
          )
        })}
      </div>
      {products.length === 0 && (
        <div className='flex justify-center items-center mt-4 mb-10'>
          <p className='text-textTitle'>Keine Produkte gefunden</p>
        </div>
      )}
      {pagination && data && setPagination && (
        <PaginationBar
          pageIndex={pagination.pageIndex}
          totalPages={totalPages ?? (data?.total_pages || null)}
          hasNext={hasNext}
          setPage={handlePageChange}
          pageSize={pagination.pageSize}
          setPageSize={handlePageSizeChange}
          className='mt-4 -mx-4 md:-mx-8'
        />
      )}
    </div>
  )
}
